import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { Loader2 } from 'lucide-react';
import { SEO } from "../components/SEO.tsx";

interface MarketStandardsContent {
  id: string;
  title: string;
  content: string;
  version: number;
  updated_at: string;
}

export function MarketStandards() {
  const [content, setContent] = useState<MarketStandardsContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .rpc('get_active_site_content', { p_content_type: 'market_standards' });

      if (error) throw error;

      if (data && data.length > 0) {
        setContent(data[0]);
      }
    } catch (err) {
      setError('Failed to load market standards');
      logger.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-600 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-yellow-600 text-sm">
          Market standards are not available at this time.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        title="Market Standards"
        description="Learn about Wayja's market creation standards and guidelines. Understand the rules for creating fair and transparent prediction markets."
        url="/market-standards"
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6 border-b pb-4">
          <h1 className="text-xl font-medium text-gray-900">{content.title}</h1>
          <p className="text-xs text-gray-500 mt-1">
            Version {content.version} • Last updated: {new Date(content.updated_at).toLocaleDateString()}
          </p>
        </div>

        <div 
          className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2 [&_li]:my-1"
          dangerouslySetInnerHTML={{ __html: content.content }}
        />

        <div className="mt-8 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            These standards apply to all markets created on Wayja. If you have any questions, please{' '}
            <a href="/contact-support" className="text-wayja-teal hover:underline">contact our support team</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

